"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { resolveSimulationTypeFromPath, uploadAudioBlobToS3 } from "@/lib/simulation-audio-upload";
import { enqueuePendingSimulationAudio } from "@/lib/pending-simulation-audio";
import { coalesceMicrophoneStart } from "@/lib/microphone/start-coalescing";
import { startAudioCapture } from "@/lib/assembly/capture";
import { requestMicrophoneStream } from "@/lib/assembly/microphone";
import { startAudioRecording } from "@/lib/recordings/browser-recorder";
import { useSharedAudioElement } from "@/context/SharedAudioPlayer";

const MicrophoneContext = createContext(null);

const MicrophoneContextProvider = ({ children }) => {
  const [microphoneOpen, setMicrophoneOpen] = useState(false);
  const [isRecordingConversation, setIsRecordingConversation] = useState(false);
  const [microphoneDeviceName, setMicrophoneDeviceName] = useState(null);
  const streamRef = useRef(null);
  const captureRef = useRef(null);
  const contextRef = useRef(null);
  const recorderRef = useRef(null);
  const recordingsRef = useRef([]);
  const recordingStopRef = useRef(Promise.resolve());
  const recordingEpochRef = useRef(0);
  const generationRef = useRef(0);
  const inFlightStartRef = useRef(null);
  const listenersRef = useRef(new Set());
  const sharedAudioRef = useSharedAudioElement();
  const subscribeAudio = useCallback(listener => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const stopConversationRecording = useCallback(() => {
    const recording = recorderRef.current;
    if (!recording) return recordingStopRef.current;
    recorderRef.current = null;
    setIsRecordingConversation(false);
    const epoch = recordingEpochRef.current;
    const stopped = recording.stop().then(blob => {
      if (blob && epoch === recordingEpochRef.current) recordingsRef.current.push(blob);
    });
    recordingStopRef.current = Promise.all([recordingStopRef.current, stopped]);
    return recordingStopRef.current;
  }, []);

  const clearConversationChunks = useCallback(() => {
    recordingEpochRef.current += 1;
    recordingsRef.current = [];
  }, []);

  const stopMicrophone = useCallback(async ({ flush = false, closeAudioContext = false } = {}) => {
    generationRef.current += 1;
    setMicrophoneOpen(false);
    setMicrophoneDeviceName(null);
    const capture = captureRef.current;
    captureRef.current = null;
    const stream = streamRef.current;
    await capture?.stop({ flush });
    stream?.getTracks().forEach(track => track.stop());
    if (streamRef.current === stream) streamRef.current = null;
    await stopConversationRecording();
    if (closeAudioContext && contextRef.current) {
      const context = contextRef.current;
      // MediaElementSource is one-shot: closing its context would silence
      // persona playback after a navigation. Keep that graph on the element.
      const element = sharedAudioRef.current;
      if (element?.__persistedAudioCtx !== context && context.state !== "closed") {
        await context.close().catch(() => {});
        contextRef.current = null;
      }
    }
  }, [stopConversationRecording, sharedAudioRef]);

  useEffect(() => {
    const element = sharedAudioRef.current;
    const saved = element?.__persistedAudioCtx;
    if (saved && saved.state !== "closed") {
      contextRef.current = saved;
      saved.resume().catch(() => {});
    }
    return () => { void stopMicrophone({ closeAudioContext: true }); };
  }, [stopMicrophone, sharedAudioRef]);

  const performMicrophoneStart = useCallback(async (recordConversation, deviceId) => {
    await stopMicrophone();
    const generation = generationRef.current;
    const cancelled = () => generation !== generationRef.current;
    let capture;
    let stream;
    try {
      const opened = await requestMicrophoneStream(deviceId);
      stream = opened.stream;
      if (cancelled()) throw new DOMException("Microphone start cancelled", "AbortError");
      const track = stream.getAudioTracks()[0];
      if (!track) throw new Error("No microphone audio track was found.");
      streamRef.current = stream;
      const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextConstructor) throw Object.assign(new Error("This browser cannot capture live audio."), { name: "NotSupportedError" });
      if (!contextRef.current || contextRef.current.state === "closed") {
        contextRef.current = new AudioContextConstructor({ latencyHint: "interactive" });
      }
      const audioContext = contextRef.current;
      let recordingSource = null;
      const element = recordConversation ? sharedAudioRef.current : null;
      if (recordConversation && !element) {
        console.warn("[Microphone] No shared audio element; the persona's voice will not be in this recording.");
      }
      if (element) {
        recordingSource = element.__persistedSystemSource;
        if (!recordingSource) {
          recordingSource = audioContext.createMediaElementSource(element);
          recordingSource.connect(audioContext.destination);
          element.__persistedAudioCtx = audioContext;
          element.__persistedSystemSource = recordingSource;
        }
      }
      capture = await startAudioCapture({
        micStream: stream, audioContext, recordingSource,
        onChunk: chunk => {
          const { pcm } = chunk;
          if (streamRef.current !== stream) return;
          if (listenersRef.current.size) listenersRef.current.forEach(listener => listener(pcm, chunk));
        },
      });
      if (cancelled()) throw new DOMException("Microphone start cancelled", "AbortError");
      captureRef.current = capture;
      if (recordConversation) {
        recorderRef.current = startAudioRecording(capture.mixedStream);
        setIsRecordingConversation(Boolean(recorderRef.current));
      }
      setMicrophoneOpen(true);
      setMicrophoneDeviceName(track.label || "Default Microphone");
      return { deviceId: track.getSettings().deviceId || null, attemptLabel: opened.attemptLabel };
    } catch (error) {
      await capture?.stop();
      stream?.getTracks().forEach(track => track.stop());
      if (streamRef.current === stream) streamRef.current = null;
      throw error;
    }
  }, [stopMicrophone, sharedAudioRef]);

  const startMicrophone = useCallback((recordConversation = false, deviceId = null) =>
    coalesceMicrophoneStart(inFlightStartRef, recordConversation, deviceId, performMicrophoneStart),
  [performMicrophoneStart]);
  const flushAudio = useCallback(() => captureRef.current?.flush() || Promise.resolve(), []);

  const startConversationRecording = useCallback(async () => {
    clearConversationChunks();
    return startMicrophone(true);
  }, [clearConversationChunks, startMicrophone]);

  const recordingBlob = useCallback(async () => {
    await recordingStopRef.current;
    const chunks = recordingsRef.current;
    return chunks.length ? new Blob(chunks, { type: chunks[0].type }) : null;
  }, []);

  const getAudioUrl = useCallback(async () => {
    const blob = await recordingBlob();
    if (!blob) return null;
    const extension = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
    try {
      return await uploadAudioBlobToS3({ blob, mimeType: blob.type, filename: `conversation.${extension}` });
    } catch (error) {
      const simulationType = resolveSimulationTypeFromPath(window.location.pathname);
      if (simulationType) {
        await enqueuePendingSimulationAudio({
          simulationType, blob, mimeType: blob.type,
          sourcePath: `${window.location.pathname}${window.location.search}`,
        }).catch(queueError => console.error("[Microphone] Could not retain recording for retry", queueError));
      }
      throw error;
    }
  }, [recordingBlob]);

  const getConversationDuration = useCallback(async () => {
    const blob = await recordingBlob();
    if (!blob) return null;
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return null;
    const context = new AudioContextConstructor();
    try {
      return (await context.decodeAudioData(await blob.arrayBuffer())).duration;
    } catch {
      return null;
    } finally {
      await context.close().catch(() => {});
    }
  }, [recordingBlob]);

  const contextValue = useMemo(() => ({
    microphone: streamRef.current, stream: streamRef.current, microphoneOpen, microphoneDeviceName,
    startMicrophone, stopMicrophone, flushAudio, subscribeAudio,
    getAudioUrl, getConversationDuration, startConversationRecording, stopConversationRecording,
    clearConversationChunks, isRecordingConversation,
  }), [microphoneOpen, microphoneDeviceName, startMicrophone, stopMicrophone, flushAudio, subscribeAudio,
    getAudioUrl, getConversationDuration,
    startConversationRecording, stopConversationRecording, clearConversationChunks, isRecordingConversation]);

  return <MicrophoneContext.Provider value={contextValue}>{children}</MicrophoneContext.Provider>;
};

const useMicrophone = () => {
  const context = useContext(MicrophoneContext);
  if (!context) throw new Error("useMicrophone must be used within a MicrophoneProvider");
  return context;
};

export { MicrophoneContextProvider, useMicrophone };
